import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import iosIllustration from '../assets/images/ios.png';
import ProposalForm from "./ProposalForm";

const BRAND_GRADIENT = "linear-gradient(135deg, #0047ff, #00c4ff)";

/* ===================== WRAPPER ===================== */

const Wrapper = styled.div`
  font-family: 'Poppins', sans-serif;
  min-height: 100vh;
  color: #e5e7eb;
  padding: 80px 16px;
  overflow-x: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

/* ===================== HERO ===================== */

const Section = styled(motion.section)`
  text-align: center;
  padding: 40px 20px;
`;

const Title = styled.h1`
  font-size: 2.4rem;
  font-weight: 700;
  margin-bottom: 20px;
  word-break: break-word;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Paragraph = styled.p`
  max-width: 820px;
  margin: 0 auto;
  font-size: 1.1rem;
  line-height: 1.6;
  color: #cbd5e1;
`;

const CtaButton = styled(motion.button)`
  margin-top: 30px;
  padding: 14px 34px;
  border-radius: 50px;
  border: none;
  background: ${BRAND_GRADIENT};
  color: #fff;
  font-weight: 600;
  font-size: 1.05rem;
  cursor: pointer;
`;

/* ===================== WHY SECTION ===================== */

const WhySection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 24px;
  max-width: 900px;
  width: 100%;
  margin: 70px auto;
  padding: 0 16px;
`;

const WhyText = styled.div`
  max-width: 720px;
`;

const WhyTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 16px;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const WhyPara = styled.p`
  font-size: 1.05rem;
  line-height: 1.7;
  color: #cbd5e1;
`;

const WhyImage = styled.img`
  width: 100%;
  max-width: 340px;
  height: auto;
  border-radius: 20px;
  display: block;
  margin: 0 auto;
`;

/* ===================== GRID ===================== */

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin: 60px auto 0;
  padding: 0 12px;
  justify-items: center;

  @media (max-width: 360px) {
    grid-template-columns: 1fr;
    padding: 0 8px;
  }
`;

const Card = styled(motion.div)`
  width: 100%;
  max-width: 320px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(14px);
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.25);
  border: 1px solid rgba(255,255,255,0.12);
`;

const CardTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 10px;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const CardText = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: #cbd5e1;
`;

/* ===================== COMPONENT ===================== */

export default function IOS() {

  const [showProposal, setShowProposal] = useState(false);

  return (
    <>
      <Wrapper>
        <Container>

          {/* HERO */}
          <Section
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
          >
            <Title>iOS App Development</Title>

            <Paragraph>
              Berrybloomer is a leading iOS app development company that provides advanced, dynamic, and top-notch iOS application
              development solutions. Our team of expert iOS developers stays updated with the latest trends and technologies to deliver
              cutting-edge apps tailored to your business needs.
            </Paragraph>

            <CtaButton
              whileHover={{
                scale: 1.08,
                boxShadow: "0 0 20px rgba(0,196,255,0.45)",
              }}
              whileTap={{ scale: 0.96 }}
              transition={{ type: 'spring', stiffness: 260, damping: 18 }}
              onClick={() => setShowProposal(true)}
            >
              Request A Proposal
            </CtaButton>
          </Section>

          {/* WHY SECTION */}
          <WhySection>
            <WhyText>
              <WhyTitle>Why Develop an iOS App?</WhyTitle>
              <WhyPara>
                iOS users are known for higher engagement and stronger purchasing power. 
                Developing an iOS application enhances brand credibility, delivers premium 
                user experience, and ensures top-tier security. With Apple's ecosystem, 
                businesses benefit from seamless performance, loyal customers, and access 
                to a global high-value audience.
              </WhyPara>
            </WhyText>

            <WhyImage
              src={iosIllustration}
              alt="iOS Development Illustration"
            />
          </WhySection>

          {/* GRID */}
          <Grid>
            {phases.map((phase) => (
              <Card
                key={phase.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                whileHover={{
                  y: -10,
                  scale: 1.04,
                  boxShadow: "0 0 30px rgba(0,196,255,0.3)",
                }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              >
                <CardTitle>{phase.title}</CardTitle>
                <CardText>{phase.description}</CardText>
              </Card>
            ))}
          </Grid>

        </Container>
      </Wrapper>

      <ProposalForm open={showProposal} setOpen={setShowProposal} />
    </>
  );
}

/* ===================== DATA ===================== */

const phases = [
  {
    title: 'Planning Phase',
    description:
      'At Berrybloomer, we strategize iOS app development projects by analyzing the business model, challenges, target audience, revenue model, etc.',
  },
  {
    title: 'Design Phase',
    description:
      'We deliver seamless and effortless UI/UX with a polished look. Our experts perform complete R&D and then design the app based on specific needs.',
  },
  {
    title: 'Development Phase',
    description:
      'With our highly skilled iOS developers in India, we pride ourselves on delivering top-quality native iOS apps.',
  },
  {
    title: 'Testing Phase',
    description:
      'We perform comprehensive testing methods to deliver a cyber-secure app along with an appealing design.',
  },
  {
    title: 'App Launch & Maintenance',
    description:
      'The launch of an app begins continuous app improvements to keep the users engaged and maintain the process.',
  },
  {
    title: 'App Consultation',
    description:
      'Our iOS experts connect with the client to understand the app’s idea, target audience, competitors, and revenue model.',
  },
];